import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import HorizontalScroll from '@components/HorizontalScroll';
import { toastSuccess } from '@components/Toast';

import AddPriceRangeItemModal from '@features/crm/brands/components/pricing/AddPriceRangeItemModal';
import PriceTable from '@features/crm/brands/components/pricing/PriceTable';
import ConfirmationModal from '@features/crm/components/ConfirmationModal';

import { fetchPriceRangeTableDataById } from '@store/actions/BrandProfileAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { deletePriceRange, getPriceRangeCategoryData } from '@services/crmService';

import { printApiError } from '@utils/Util';

const PriceConfigurationTables = ({ priceRangeTableData, profitMarginTableData }) => {
	const [showAddModal, setShowAddModal] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [priceRangeIds, setPriceRangeIds] = useState([]);
	const [saveType, setSaveType] = useState('add');
	const [selectedId, setSelectedId] = useState(null);
	const [categoryResponseData, setCategoryResponseData] = useState(null);
	const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);
	const dispatch = useDispatch();
	const { id } = useParams();
	const priceRangeRef = useRef(null);
	const profitMarginRef = useRef(null);

	const onEditClick = async (categoryId) => {
		dispatch(openLoader());
		setSelectedId(categoryId);
		setShowAddModal(true);
		setSaveType('edit');
		try {
			let response = await getPriceRangeCategoryData(id, categoryId);
			setCategoryResponseData(response);
			dispatch(closeLoader());
		} catch {
			setShowAddModal(false);
		}
	};

	const onCloseModal = () => {
		setShowAddModal(false);
		setSelectedId(null);
		setCategoryResponseData(null);
		setSaveType('add');
		setPriceRangeIds([]);
	};

	// Extract unique fabric types
	const fabricTypes = [
		...new Set(
			priceRangeTableData?.flatMap((category) =>
				category.fabricPriceRangeList?.map((fabric) => fabric.fabricType)
			)
		)
	];

	// Group markets by fabric type, ensuring no duplication
	const marketsForFabricTypes = fabricTypes.reduce((acc, fabricType) => {
		acc[fabricType] = [
			...new Map(
				priceRangeTableData.flatMap((category) =>
					category.fabricPriceRangeList
						.filter((fabric) => fabric.fabricType === fabricType)
						.flatMap((fabric) =>
							fabric.priceRangeList.map((priceRange) => [
								priceRange.marketResponse.id,
								{ id: priceRange.marketResponse.id, value: priceRange.marketResponse.name }
							])
						)
				)
			).values()
		];
		return acc;
	}, {});

	const getSelectedCategoryIds = () => {
		return priceRangeTableData.map((item) => item.categoryResponse.id);
	};

	const getPriceRangeIdsToDelete = () => {
		const selectedCategory = priceRangeTableData?.find(
			(category) => category.categoryResponse.id === selectedId
		);

		if (!selectedCategory) {
			return [];
		}

		const priceRangeIds = selectedCategory.fabricPriceRangeList?.flatMap((fabric) =>
			fabric.priceRangeList?.map((priceRange) => priceRange.id)
		);

		setPriceRangeIds(priceRangeIds);
	};

	const onDeleteCategory = async () => {
		dispatch(openLoader());
		try {
			await deletePriceRange(id, priceRangeIds);
			dispatch(closeLoader());
			setShowConfirmationModal(false);
			dispatch(fetchPriceRangeTableDataById(`${id}`));
			toastSuccess('Successfully Deleted');
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const onDeleteClick = (id) => {
		setShowConfirmationModal(true);
		setSelectedId(id);
	};

	const onCloseConfirmationModal = () => {
		setShowConfirmationModal(false);
		setSelectedId(null);
		setPriceRangeIds([]);
	};

	useEffect(() => {
		if (selectedId) {
			getPriceRangeIdsToDelete();
		}
	}, [selectedId]);

	useEffect(() => {
		if (priceRangeTableData?.length > 0) {
			setShowHorizontalScroll(true);
		}
	}, [priceRangeTableData]);

	return (
		<div className="pricing-tab-container">
			<div className="price-range-container mt-3">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<h3 className="semibold-16 mb-0">Buying Price Range</h3>

					{priceRangeTableData?.length !== 0 && (
						<button
							className="button text size32 px-0 d-flex align-items-center"
							onClick={() => setShowAddModal(true)}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/add-round.svg`} alt="add" />
							<span className="ml-1">Add Item</span>
						</button>
					)}
				</div>
				{showHorizontalScroll && (
					<div className="d-flex justify-content-end">
						<HorizontalScroll elementReference={priceRangeRef.current} />
					</div>
				)}
				<div className="price-range scroll-x-label" ref={priceRangeRef}>
					{priceRangeTableData?.length > 0 ? (
						<PriceTable
							extraClass={'price-range'}
							tableData={priceRangeTableData}
							fabricTypes={fabricTypes}
							marketsForFabricTypes={marketsForFabricTypes}
							isEditable={false}
							onEditClick={onEditClick}
							onDeleteClick={onDeleteClick}
						/>
					) : (
						<div className="alert alert-secondary text-center">
							<p>No data found</p>
							<button
								className="button outline size32 mt-2"
								onClick={() => setShowAddModal(true)}
							>
								<img src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`} alt="add" />
								<span>Add Item</span>
							</button>
						</div>
					)}
				</div>
			</div>

			<div className="profit-margin-container mt-4">
				<h3 className="semibold-16 mb-12">Profit Margin</h3>
				{showHorizontalScroll && (
					<div className="d-flex justify-content-end">
						<HorizontalScroll elementReference={profitMarginRef.current} />
					</div>
				)}
				<div className="profit-margin scroll-x-label" ref={profitMarginRef}>
					{priceRangeTableData?.length > 0 ? (
						<PriceTable
							extraClass={'profit-margin'}
							tableData={profitMarginTableData}
							fabricTypes={fabricTypes}
							marketsForFabricTypes={marketsForFabricTypes}
							isEditable={true}
							onEditClick={null}
						/>
					) : (
						<div className="alert alert-secondary text-center">
							<p>No data found</p>
						</div>
					)}
				</div>
			</div>

			{showAddModal && (
				<AddPriceRangeItemModal
					saveType={saveType}
					idToEdit={selectedId}
					onCloseModal={onCloseModal}
					selectedCategoryIds={getSelectedCategoryIds()}
					categoryResponseData={categoryResponseData}
					priceRangeIds={priceRangeIds}
				/>
			)}
			{showConfirmationModal && (
				<ConfirmationModal
					title={'Delete Category'}
					closeModal={onCloseConfirmationModal}
					description={' about deleting this category?'}
					callback={onDeleteCategory}
					footerType={'CLOSE'}
				/>
			)}
		</div>
	);
};

export default PriceConfigurationTables;
